import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { playTestSound, stopAllAudio } from '../utils/audioUtils';
import { saveToStorage, loadFromStorage, STORAGE_KEYS } from '../utils/localStorage';

export function useRadioAlarm(currentTime) {
  // 加载收音机闹钟设置
  const loadSettings = () => {
    const defaults = {
      alarmHour: '07',
      alarmMinute: '00',
      snoozeMinutes: '05',
      selectedMelody: 'CLASSIC',
      growing: false
    };
    
    return loadFromStorage(STORAGE_KEYS.RADIO_ALARM_SETTINGS, defaults);
  };

  // 加载收音机特定设置
  const loadRadioSettings = () => {
    const defaults = {
      selectedStation: 'jazz',
      volume: 70
    };
    return loadFromStorage('radio_alarm_settings', defaults);
  };

  // 加载收音机闹钟激活状态
  const loadActiveState = () => {
    const defaults = { isActive: false, activatedAt: null };
    return loadFromStorage(STORAGE_KEYS.RADIO_ALARM_ACTIVE_STATE, defaults);
  };

  const savedSettings = loadSettings();
  const savedRadioSettings = loadRadioSettings();
  const savedActiveState = loadActiveState();

  const [alarmHour, setAlarmHour] = useState(savedSettings.alarmHour);
  const [alarmMinute, setAlarmMinute] = useState(savedSettings.alarmMinute);
  const [snoozeMinutes, setSnoozeMinutes] = useState(savedSettings.snoozeMinutes);
  const [selectedMelody, setSelectedMelody] = useState(savedSettings.selectedMelody);
  const [growing, setGrowing] = useState(savedSettings.growing);
  const [alarmActive, setAlarmActive] = useState(savedActiveState.isActive);
  const [alarmRinging, setAlarmRinging] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  // 收音机相关状态
  const [selectedStation, setSelectedStation] = useState(savedRadioSettings.selectedStation);
  const [volume, setVolume] = useState(savedRadioSettings.volume);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioControlRef = useRef(null);
  const radioAudioRef = useRef(null);
  const timeSettingsRef = useRef({ alarmHour: savedSettings.alarmHour, alarmMinute: savedSettings.alarmMinute });
  const isSnoozingRef = useRef(false);

  // 收音机电台列表
  const radioStations = useMemo(() => [
    { 
      id: 'talksport', 
      name: 'TalkSport', 
      url: 'https://radio.talksport.com/stream',
      genre: 'Sports/News'
    },
    { 
      id: 'capital', 
      name: 'Capital FM', 
      url: 'https://media-ice.musicradio.com/CapitalMP3',
      genre: 'Pop'
    },
    { 
      id: 'espn1', 
      name: 'ESPN Radio', 
      url: 'https://stream.revma.ihrhls.com/zc209',
      genre: 'Sports'
    },
    { 
      id: 'espn2', 
      name: 'ESPN Radio 2', 
      url: 'https://stream.revma.ihrhls.com/zc7729',
      genre: 'Sports'
    },
    { 
      id: 'zeno', 
      name: 'Zeno FM', 
      url: 'https://stream.zeno.fm/f3wvbbqmdg8uv',
      genre: 'Music'
    },
    { 
      id: 'talkradio', 
      name: 'Talk Radio UK', 
      url: 'https://radio.talkradio.co.uk/stream',
      genre: 'Talk/News'
    },
    { 
      id: 'jazz', 
      name: 'Smooth Jazz', 
      url: 'https://streams.calmradio.com/api/39/128/stream',
      genre: 'Jazz'
    },
    { 
      id: 'classical', 
      name: 'Classical Music', 
      url: 'https://streams.calmradio.com/api/39/128/stream',
      genre: 'Classical'
    },
    { 
      id: 'ambient', 
      name: 'Ambient Sounds', 
      url: 'https://streams.calmradio.com/api/39/128/stream',
      genre: 'Ambient'
    },
    { 
      id: 'nature', 
      name: 'Nature Sounds', 
      url: 'https://streams.calmradio.com/api/39/128/stream',
      genre: 'Nature'
    },
    { 
      id: 'chillout', 
      name: 'ChillOut Radio', 
      url: 'https://streams.calmradio.com/api/39/128/stream',
      genre: 'Chill'
    }
  ], []);

  // 初始化收音机音频元素
  useEffect(() => {
    if (!radioAudioRef.current) {
      radioAudioRef.current = new Audio();
      radioAudioRef.current.preload = 'none';
    }
    
    return () => {
      if (radioAudioRef.current) {
        radioAudioRef.current.pause();
        radioAudioRef.current = null;
      }
    };
  }, []);

  // 收音机播放控制函数
  const playRadio = useCallback(() => {
    if (radioAudioRef.current && selectedStation) {
      const station = radioStations.find(s => s.id === selectedStation);
      if (station) {
        radioAudioRef.current.src = station.url;
        radioAudioRef.current.volume = volume / 100;
        radioAudioRef.current.play().catch(e => console.error('📻 收音机播放失败:', e));
        setIsPlaying(true);
        console.log('📻 [收音机闹钟] 开始播放收音机:', station.name);
      }
    }
  }, [selectedStation, volume, radioStations]);

  const stopRadio = useCallback(() => {
    if (radioAudioRef.current) {
      radioAudioRef.current.pause();
      radioAudioRef.current.currentTime = 0;
      setIsPlaying(false);
      console.log('📻 [收音机闹钟] 停止播放收音机');
    }
  }, []);

  // 保存激活状态到 localStorage
  const saveActiveState = useCallback((isActive) => {
    const activeState = {
      isActive,
      activatedAt: isActive ? Date.now() : null
    };
    
    saveToStorage(STORAGE_KEYS.RADIO_ALARM_ACTIVE_STATE, activeState);
  }, []);

  // 自定义的 setAlarmActive 函数，同时保存状态
  const setAlarmActiveWithSave = useCallback((isActive) => {
    setAlarmActive(isActive);
    saveActiveState(isActive);
  }, [saveActiveState]);

  // 简化的保存逻辑
  const isInitialMount = useRef(true);
  // 保存设置的逻辑
  useEffect(() => {
    // 跳过初始挂载时的保存
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const settings = {
      alarmHour,
      alarmMinute,
      snoozeMinutes,
      selectedMelody,
      growing
    };
    
    saveToStorage(STORAGE_KEYS.RADIO_ALARM_SETTINGS, settings);
  }, [alarmHour, alarmMinute, snoozeMinutes, selectedMelody, growing]);

  // 保存收音机特定设置
  useEffect(() => {
    // 跳过初始挂载时的保存
    if (isInitialMount.current) {
      return;
    }
    
    const radioSettings = {
      selectedStation,
      volume
    };
    
    saveToStorage('radio_alarm_settings', radioSettings);
  }, [selectedStation, volume]);

  // 监听时间设置变化，重置激活状态（但排除 snooze 操作）
  useEffect(() => {
    // 跳过初始挂载
    if (isInitialMount.current) {
      return;
    }
    
    // 如果是 snooze 操作，跳过重置
    if (isSnoozingRef.current) {
      isSnoozingRef.current = false;
      timeSettingsRef.current = { alarmHour, alarmMinute };
      return;
    }
    
    // 检查时间设置是否发生变化
    if (timeSettingsRef.current.alarmHour !== alarmHour || timeSettingsRef.current.alarmMinute !== alarmMinute) {
      console.log('📻 [收音机闹钟] 时间设置已更改，重置激活状态');
      setAlarmActiveWithSave(false);
      setAlarmRinging(false);
      
      // 停止当前音频
      if (audioControlRef.current) {
        audioControlRef.current.stop();
        audioControlRef.current = null;
      }
      stopAllAudio();
      
      // 停止收音机播放
      stopRadio();
      
      // 更新引用
      timeSettingsRef.current = { alarmHour, alarmMinute };
    }
  }, [alarmHour, alarmMinute, setAlarmActiveWithSave, stopRadio]);

  const triggerAlarm = useCallback(() => {
    // 只在 radio 页面时触发收音机闹钟
    if (typeof window !== 'undefined' && window.location.pathname !== '/radio-alarm') {
      console.log('📻 [收音机闹钟] 不在 radio 页面，跳过触发');
      return;
    }
    
    console.log('📻 [收音机闹钟] 触发闹钟 - 只播放收音机');
    setAlarmRinging(true);
    // 收音机闹钟只播放收音机，不播放额外的闹钟铃声
  }, []);

  // 监听alarmRinging状态变化来控制收音机播放
  useEffect(() => {
    if (alarmRinging) {
      playRadio();
    } else {
      // 当闹钟停止时，也停止收音机播放
      stopRadio();
    }
  }, [alarmRinging, playRadio, stopRadio]);

  // 初始化时检查激活状态和时间
  useEffect(() => {
    if (savedActiveState.isActive && savedActiveState.activatedAt) {
      console.log('🔄 [收音机闹钟] 检查激活的闹铃状态...');
      
      const now = new Date();
      const alarm = new Date();
      alarm.setHours(parseInt(alarmHour), parseInt(alarmMinute), 0, 0);
      
      // 如果闹铃时间已经过了，设置为明天
      if (alarm <= now) {
        alarm.setDate(alarm.getDate() + 1);
      }
      
      // 检查是否应该立即响铃（允许1分钟的误差）
      const timeDiff = now - alarm;
      if (timeDiff >= 0 && timeDiff <= 60000) { // 1分钟内
        console.log('📻 [收音机闹钟] 闹铃时间已到，立即触发');
        triggerAlarm();
      } else {
        console.log('✅ [收音机闹钟] 闹铃仍在激活状态，继续倒计时');
      }
    }
  }, [triggerAlarm, savedActiveState.isActive, savedActiveState.activatedAt, alarmHour, alarmMinute]);

  // Calculate time left until alarm
  useEffect(() => {
    if (alarmActive) {
      const now = new Date();
      const alarm = new Date();
      alarm.setHours(parseInt(alarmHour), parseInt(alarmMinute), 0, 0);
      
      if (alarm <= now) {
        alarm.setDate(alarm.getDate() + 1);
      }
      
      const diff = alarm - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      
      // Check if alarm should ring
      if (hours === 0 && minutes === 0 && seconds === 0) {
        triggerAlarm();
      }
    }
  }, [currentTime, alarmActive, alarmHour, alarmMinute, triggerAlarm]);

  const stopAlarm = () => {
    console.log('🛑 [收音机闹钟] 停止闹钟');
    setAlarmRinging(false);
    setAlarmActiveWithSave(false);
    
    // 停止当前播放的音频
    if (audioControlRef.current) {
      audioControlRef.current.stop();
      audioControlRef.current = null;
    }
    
    // 确保停止所有音频
    stopAllAudio();
    
    // 停止收音机播放
    stopRadio();
  };

  const snoozeAlarm = () => {
    console.log('😴 [收音机闹钟] 贪睡闹钟');
    setAlarmRinging(false);
    
    // 停止当前音频
    if (audioControlRef.current) {
      audioControlRef.current.stop();
      audioControlRef.current = null;
    }
    stopAllAudio();
    
    // 停止收音机播放
    stopRadio();
    
    // 计算新的闹钟时间
    const newMinute = (parseInt(alarmMinute) + parseInt(snoozeMinutes)) % 60;
    const newHour = (parseInt(alarmHour) + Math.floor((parseInt(alarmMinute) + parseInt(snoozeMinutes)) / 60)) % 24;
    const newMinuteStr = newMinute.toString().padStart(2, '0');
    const newHourStr = newHour.toString().padStart(2, '0');
    
    // 设置 snooze 标志，防止时间变化监听器重置激活状态
    isSnoozingRef.current = true;
    
    // 更新时间
    setAlarmMinute(newMinuteStr);
    setAlarmHour(newHourStr);
    
    // 确保闹钟保持激活状态并保存
    if (!alarmActive) {
      setAlarmActiveWithSave(true);
    } else {
      // 即使已经是激活状态，也重新保存以确保状态同步
      saveActiveState(true);
    }
    
    // 更新时间引用
    timeSettingsRef.current = { alarmHour: newHourStr, alarmMinute: newMinuteStr };
  };

  const testSound = () => {
    console.log(`🔊 [收音机闹钟] 测试音效: ${selectedMelody}`);
    // 使用新的测试音效函数，播放当前选中的音效
    playTestSound(selectedMelody, 2000).then(audioControl => {
      console.log(`正在测试音效: ${selectedMelody}`);
    }).catch(error => {
      console.error('[收音机闹钟] 测试音效播放失败:', error);
    });
  };

  const testRadio = () => {
    if (isPlaying) {
      stopRadio();
    } else {
      playRadio();
    }
  };

  return {
    alarmHour,
    alarmMinute,
    snoozeMinutes,
    selectedMelody,
    growing,
    alarmActive,
    alarmRinging,
    timeLeft,
    setAlarmHour,
    setAlarmMinute,
    setSnoozeMinutes,
    setSelectedMelody,
    setGrowing,
    setAlarmActive: setAlarmActiveWithSave,
    stopAlarm,
    snoozeAlarm,
    testSound,
    // 收音机相关
    selectedStation,
    setSelectedStation,
    volume,
    setVolume,
    isPlaying,
    radioStations,
    playRadio,
    stopRadio,
    testRadio
  };
}